/**
 * Phase-C: Inline session/batch edit dialog with scope toggle + conflict preflight.
 *
 * - "This Instance Only"   → PUT /api/sessions/{session_id}
 * - "This and Future"      → PUT /api/batches/{batch_id}?change_scope=future_only
 *
 * Uses POST /api/scheduling/check-conflicts before save.
 * Blocking conflicts → save disabled + red banner.
 * Warning conflicts (e.g. instructor double-book) → "Acknowledge & Save" button.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, AlertTriangle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import TimeInput from "@/components/TimeInput";

export const EditSessionDialog = ({
  open,
  onOpenChange,
  calendarItem,           // class item from /calendar response (has batch_id, session_id, room_id, start_time, end_time, instructor_id)
  studioId,
  studioRooms,            // [{room_id, name, capacity}]
  instructors,            // [{user_id|instructor_id, name}]
  batchDefaults,          // optional: {room_id, schedule[], instructor_id} for "this & future" reference
  selectedDate,           // ISO yyyy-mm-dd for conflict preflight
  onSaved,                // callback after successful save
}) => {
  const [scope, setScope] = useState("this_only");
  const [form, setForm] = useState({
    room_id: "", start_time: "", end_time: "", instructor_id: "",
  });
  const [validation, setValidation] = useState(null); // {ok, blocking, warnings}
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open && calendarItem) {
      setForm({
        room_id: calendarItem.room_id || "",
        start_time: calendarItem.start_time || "",
        end_time: calendarItem.end_time || "",
        instructor_id: calendarItem.instructor_id || "",
      });
      setScope("this_only");
      setValidation(null);
    }
  }, [open, calendarItem]);

  // Live preflight against /api/scheduling/check-conflicts
  const runPreflight = async () => {
    if (!form.room_id || !form.start_time || !form.end_time) return;
    setValidating(true);
    try {
      const res = await api.post("/scheduling/check-conflicts", {
        studio_id: studioId,
        room_id: form.room_id,
        date: new Date(selectedDate).toISOString(),
        start_time: form.start_time,
        end_time: form.end_time,
        instructor_id: form.instructor_id || null,
        exclude_session_id: calendarItem?.session_id || null,
      });
      setValidation(res.data);
    } catch (e) {
      setValidation(null);
    } finally {
      setValidating(false);
    }
  };

  // Debounced preflight on form change
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(runPreflight, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.room_id, form.start_time, form.end_time, form.instructor_id, open]);

  const dirty = useMemo(() => {
    if (!calendarItem) return false;
    return (
      form.room_id !== (calendarItem.room_id || "") ||
      form.start_time !== (calendarItem.start_time || "") ||
      form.end_time !== (calendarItem.end_time || "") ||
      form.instructor_id !== (calendarItem.instructor_id || "")
    );
  }, [form, calendarItem]);

  const blocking = validation?.blocking || [];
  const warnings = validation?.warnings || [];
  const canSave = dirty && blocking.length === 0;
  const needsAcknowledge = warnings.length > 0;

  const doSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (scope === "this_only") {
        // Idempotent occurrence update — backend materialises the session on
        // first use, so the frontend never has to know whether a sessions
        // row already exists. Endpoint key = (batch_id, date).
        await api.put(
          `/sessions/occurrence/${calendarItem.batch_id}/${selectedDate}`,
          {
            room_id: form.room_id,
            start_time: form.start_time,
            end_time: form.end_time,
            instructor_id: form.instructor_id || null,
            acknowledge_warnings: needsAcknowledge,
          }
        );
        toast.success("Session updated for this instance only");
      } else {
        // "this and future" — patch the batch with new room/timing/instructor
        if (!batchDefaults) {
          toast.error("Batch defaults not available — refresh and retry");
          setSaving(false);
          return;
        }
        // Build new schedule preserving the existing day list, but updating timing for the
        // weekday matching this calendar item.
        const dayOfWeek = new Date(selectedDate).toLocaleDateString("en-US", { weekday: "long" });
        const newSchedule = (batchDefaults.schedule || []).map((s) =>
          s.day === dayOfWeek
            ? { ...s, start_time: form.start_time, end_time: form.end_time }
            : s
        );
        await api.put(
          `/batches/${calendarItem.batch_id}?change_scope=future_only`,
          {
            ...batchDefaults,
            room_id: form.room_id,
            instructor_id: form.instructor_id || "",
            schedule: newSchedule,
          },
        );
        toast.success("Batch updated for this and all future sessions");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Failed to save");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-session-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Edit Class</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Scope toggle */}
          <div className="bg-violet-50 rounded-xl p-3 border border-violet-100">
            <Label className="text-xs font-semibold text-violet-900 mb-2 block">Apply this change to:</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="scope-this-only-btn"
                onClick={() => setScope("this_only")}
                className={`text-left p-2 rounded-lg text-xs border-2 transition-colors ${scope === "this_only" ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-200 hover:border-violet-300"}`}
              >
                <div className="font-semibold">This Instance Only</div>
                <div className="opacity-80 mt-0.5">Override just this date.</div>
              </button>
              <button
                type="button"
                data-testid="scope-future-btn"
                onClick={() => setScope("future_only")}
                className={`text-left p-2 rounded-lg text-xs border-2 transition-colors ${scope === "future_only" ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-200 hover:border-violet-300"}`}
              >
                <div className="font-semibold">This & Future</div>
                <div className="opacity-80 mt-0.5">Update batch defaults.</div>
              </button>
            </div>
          </div>

          {/* Editable fields */}
          <div className="space-y-2">
            <div className="space-y-1">
              <Label className="text-xs">Room *</Label>
              <select
                data-testid="edit-session-room-select"
                value={form.room_id}
                onChange={(e) => setForm({ ...form, room_id: e.target.value })}
                className="w-full h-10 rounded-xl border px-3 bg-white text-sm"
              >
                {(studioRooms || []).map((r) => (
                  <option key={r.room_id} value={r.room_id}>{r.name} (cap {r.capacity})</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Start *</Label>
                <TimeInput
                  data-testid="edit-session-start-time"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">End *</Label>
                <TimeInput
                  data-testid="edit-session-end-time"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Instructor</Label>
              <select
                data-testid="edit-session-instructor-select"
                value={form.instructor_id}
                onChange={(e) => setForm({ ...form, instructor_id: e.target.value })}
                className="w-full h-10 rounded-xl border px-3 bg-white text-sm"
              >
                <option value="">— Unassigned —</option>
                {(instructors || []).map((i) => (
                  <option key={i.user_id || i.instructor_id} value={i.user_id || i.instructor_id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Conflicts panel */}
          {validating && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3 h-3 animate-spin" /> Checking conflicts…
            </div>
          )}
          {!validating && validation && blocking.length > 0 && (
            <div data-testid="blocking-conflicts" className="bg-red-50 border border-red-200 rounded-xl p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-red-900">Conflict — cannot save</p>
                  <ul className="mt-1 space-y-0.5">
                    {blocking.map((c, i) => (
                      <li key={`${c.start_time}-${c.end_time}-${c.with || i}`} className="text-[11px] text-red-700">
                        {c.start_time}–{c.end_time}: {c.with}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          {!validating && validation && warnings.length > 0 && blocking.length === 0 && (
            <div data-testid="warning-conflicts" className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-amber-900">Soft conflict — confirm to proceed</p>
                  <ul className="mt-1 space-y-0.5">
                    {warnings.map((w, i) => (
                      <li key={`${w.start_time}-${w.end_time}-${w.with || i}`} className="text-[11px] text-amber-700">
                        {w.start_time}–{w.end_time}: {w.with}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1 rounded-xl"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="save-session-edit-btn"
              className={`flex-1 rounded-xl ${needsAcknowledge ? "bg-amber-600 hover:bg-amber-700" : "bg-violet-600 hover:bg-violet-700"} text-white`}
              onClick={doSave}
              disabled={!canSave || saving}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {needsAcknowledge ? "Acknowledge & Save" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditSessionDialog;
